import type { Kks, PidStatus } from "@shared/types/domain";
import type React from "react";
import { useEffect } from "react";

interface SaveMut {
  isPending: boolean;
  mutate: () => void;
}

interface Params {
  shortcutsEnabled: boolean;
  pidLocked: boolean | undefined;
  pidStatus: PidStatus | undefined;
  isSuperUser: boolean;
  navigateNext: () => void;
  navigatePrev: () => void;
  updateKksItem: (id: string, updates: Partial<Kks>) => void;
  deleteKksItem: (id: string) => void;
  setDrawing: React.Dispatch<React.SetStateAction<boolean>>;
  setConfirmDeleteIds: React.Dispatch<React.SetStateAction<Set<string> | null>>;
  setLockedAlertOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dirtyRef: React.MutableRefObject<boolean>;
  saveMutRef: React.MutableRefObject<SaveMut>;
  selectedIdxRef: React.MutableRefObject<number | null>;
  sortedRef: React.MutableRefObject<Kks[]>;
  isMultiSelectRef: React.MutableRefObject<boolean>;
  selectedKksIdsRef: React.MutableRefObject<Set<string>>;
}

export function useKeyboardShortcuts({
  shortcutsEnabled,
  pidLocked,
  pidStatus,
  isSuperUser,
  navigateNext,
  navigatePrev,
  updateKksItem,
  deleteKksItem,
  setDrawing,
  setConfirmDeleteIds,
  setLockedAlertOpen,
  dirtyRef,
  saveMutRef,
  selectedIdxRef,
  sortedRef,
  isMultiSelectRef,
  selectedKksIdsRef,
}: Params): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl+S: save (always active, regardless of shortcutsEnabled)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (pidLocked) {
          setLockedAlertOpen(true);
          return;
        }
        if (dirtyRef.current && !saveMutRef.current.isPending) {
          saveMutRef.current.mutate();
        }
        return;
      }

      if (!shortcutsEnabled) return;

      // Allow typing in text inputs and textareas (but not checkboxes)
      if (
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLInputElement && e.target.type !== "checkbox")
      ) {
        return;
      }

      const idx = selectedIdxRef.current;
      const kk = idx !== null ? (sortedRef.current[idx] ?? null) : null;

      const isLocked = !!pidLocked;
      const isFrozen =
        !isSuperUser &&
        !!kk?.required_spof &&
        pidStatus !== "required_spof_review";

      const key = e.key.toLowerCase();

      switch (key) {
        case "arrowdown":
        case "arrowright":
          e.preventDefault();
          navigateNext();
          break;
        case "arrowup":
        case "arrowleft":
          e.preventDefault();
          navigatePrev();
          break;
        case "1":
        case "2":
        case "3":
        case "4":
          e.preventDefault();
          if (kk && !isLocked && !isFrozen)
            updateKksItem(kk.id, { impact: Number(e.key) });
          break;
        case "r":
          e.preventDefault();
          if (kk && !isLocked && !isFrozen)
            updateKksItem(kk.id, { required: !kk.required });
          break;
        case "v":
          e.preventDefault();
          if (kk && !isLocked && !isFrozen)
            updateKksItem(kk.id, { verified: !kk.verified });
          break;
        case "s":
          e.preventDefault();
          if (kk && !isLocked && !isFrozen)
            updateKksItem(kk.id, { spof: !kk.spof });
          break;
        case "a":
          e.preventDefault();
          if (kk && kk.spof && !isLocked && !isFrozen)
            updateKksItem(kk.id, { spof_approved: !kk.spof_approved });
          break;
        case "p":
          e.preventDefault();
          if (kk && !isLocked && !isFrozen)
            updateKksItem(kk.id, { stamp_on_pdf: !kk.stamp_on_pdf });
          break;
        case "q":
          e.preventDefault();
          if (kk && kk.spof && !isLocked && !isFrozen) {
            const canEdit =
              isSuperUser ||
              pidStatus === "required_spof_review" ||
              pidStatus === "processing" ||
              pidStatus === "error" ||
              pidStatus === undefined;
            if (canEdit)
              updateKksItem(kk.id, { required_spof: !kk.required_spof });
          }
          break;
        case "n":
          e.preventDefault();
          if (!isLocked) setDrawing((prev) => !prev);
          break;
        case "delete":
          e.preventDefault();
          if (isMultiSelectRef.current) {
            if (!pidLocked) {
              const ids = new Set(selectedKksIdsRef.current);
              if (ids.size > 1) {
                setConfirmDeleteIds(ids);
              } else {
                const singleId = ids.values().next().value;
                if (singleId) deleteKksItem(singleId);
              }
            }
          } else if (kk && !isLocked && !isFrozen) {
            deleteKksItem(kk.id);
          }
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    shortcutsEnabled,
    navigateNext,
    navigatePrev,
    updateKksItem,
    deleteKksItem,
    isSuperUser,
    pidLocked,
    pidStatus,
  ]);
}
